import React, { useState, useEffect } from "react";
import { Alert, Dropdown } from "react-bootstrap";
import { Link } from "react-router-dom";

import { emojis } from "../bootstrap/alertData";

import {
  deleteUser,
  getInspectionUsersData,
  getUserPermissions,
  getUserRole,
  signUpInspectionUser,
  updateInspectionUser,
} from "../../../APIS";
import { Can } from "../../../context/Can";

const InspectionUser = () => {
  const roles = [
    { role: "admin", level: 1 },
    { role: "moderator", level: 2 },
    { role: "electrical engineer", level: 3 },
    { role: "mechanical engineer", level: 4 },
    { role: "weather forecaster", level: 5 },
    { role: "electrical staff", level: 6 },
    { role: "mechanical staff", level: 7 },
    { role: "store keeper", level: 8 },
  ];

  const role = getUserRole();
  const { module, service } = getUserPermissions();
  const currentRole = roles.find(
    (r) => r.role.toLowerCase() === (role || "").toLowerCase(),
  );

  const [reportData, setReportData] = useState({ data: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);
  const [formData, setFormData] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const itemsPerPage = 15;
  const storedUser = sessionStorage.getItem("userDetails");
  let token = null;
  if (storedUser) {
    const userDetails = JSON.parse(storedUser);
    token = userDetails?.token;
  }

  const fetchReports = async () => {
    setLoading(true);
    try {
      const data = await getInspectionUsersData(token);
      console.log(data);
      setReportData(data);
    } catch (err) {
      setError("Failed to fetch InspectionUser.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleEdit = (user) => {
    setFormData(user);
    setIsEditMode(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      const response = await deleteUser(id, token);
      console.log(response);
      if (response.status == 200) {
        setSuccessMsg("User deleted successfully");
        fetchReports();
      }
    } catch (err) {
      setError("Failed to delete user.");
    } finally {
      setTimeout(() => {
        setSuccessMsg(null);
        setError(null);
      }, 3000);
    }
  };

  const handleAdd = () => {
    setFormData({});
    setIsEditMode(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Capitalize role value before saving
    if (name === "role") {
      const capitalized =
        value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
      setFormData((prev) => ({ ...prev, [name]: capitalized }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { _id, createdAt, updatedAt, ...cleanedData } = formData;
      if (_id) {
        await updateInspectionUser(_id, cleanedData, token);
        setSuccessMsg("User updated successfully.");
      } else {
        await signUpInspectionUser(cleanedData, token);
        setSuccessMsg("OTP has been sent to your email.");
      }
      fetchReports();
      setIsEditMode(false);
    } catch (err) {
      console.error("Error submitting form:", err);
      setError("Failed to save user.");
    } finally {
      setTimeout(() => {
        setError(null);
        setSuccessMsg(null);
      }, 4000);
    }
  };

  const filteredData = reportData.data.filter((user) => {
    const lowerSearch = searchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(lowerSearch) ||
      user.email?.toLowerCase().includes(lowerSearch) ||
      (user.isActive ? "active" : "inactive").includes(lowerSearch)
    );
  });

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const currentItems = filteredData.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage,
  );

  // Determine which roles the current user can assign
  const availableRoles =
    service === "*" && role === "*"
      ? roles
      : roles.filter((r) => r.level > (currentRole?.level || 0));
  // {
  //   console.log(service, currentRole, role, roles);
  // }

  return (
    <div className="col-12">
      {successMsg && (
        <Alert
          variant="success"
          dismissible
          onClose={() => setSuccessMsg(null)}
        >
          {emojis.success}
          {successMsg}
        </Alert>
      )}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {emojis.warning}
          {error}
        </Alert>
      )}

      {isEditMode ? (
        // Edit/Add form
        <div className="card">
          <div className="card-header">
            <h4 className="card-title">
              {formData._id ? "Edit User" : "Add User"}
            </h4>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-xl-6">
                  <div className="mb-3 row">
                    <label className="col-lg-4 col-form-label">
                      Username<span className="text-danger">*</span>
                    </label>
                    <div className="col-lg-6">
                      <input
                        type="text"
                        name="name"
                        value={formData.name || ""}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="Enter a username.."
                      />
                    </div>
                  </div>

                  <div className="mb-3 row">
                    <label className="col-lg-4 col-form-label">
                      Email<span className="text-danger">*</span>
                    </label>
                    <div className="col-lg-6">
                      <input
                        type="email"
                        name="email"
                        value={formData.email || ""}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="Your valid email.."
                      />
                    </div>
                  </div>

                  {formData._id && (
                    <div className="mb-3 row">
                      <label className="col-lg-4 col-form-label">
                        Device Token
                      </label>
                      <div className="col-lg-6">
                        <input
                          type="text"
                          name="deviceToken"
                          value={formData.deviceToken || ""}
                          onChange={handleChange}
                          className="form-control"
                          placeholder="Your device Token"
                        />
                      </div>
                    </div>
                  )}

                  <div className="mb-3 row">
                    <div className="col-lg-8 ms-auto">
                      <button type="submit" className="btn btn-secondary">
                        Submit
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary ms-2"
                        onClick={() => setIsEditMode(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>

                <div className="col-xl-6">
                  {/* Updated Role Dropdown with capitalization */}
                  <div className="mb-3 row">
                    <label className="col-lg-4 col-form-label">
                      Role<span className="text-danger">*</span>
                    </label>
                    <div className="col-lg-6">
                      <select
                        name="role"
                        value={formData.role || ""}
                        onChange={handleChange}
                        className="form-control"
                        required
                      >
                        <option value="">Select Role</option>
                        {availableRoles.map((r) => (
                          <option key={r.role} value={r.role}>
                            {r.role.charAt(0).toUpperCase() + r.role.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {formData._id && (
                    <>
                      <div className="mb-3 row">
                        <label className="col-lg-4 col-form-label">
                          Password<span className="text-danger">*</span>
                        </label>
                        <div className="col-lg-6">
                          <input
                            type="password"
                            name="password"
                            value={formData.password || ""}
                            onChange={handleChange}
                            className="form-control"
                            placeholder="Choose a safe one.."
                          />
                        </div>
                      </div>
                      <div className="mb-3 row">
                        <label className="col-lg-4 col-form-label">
                          Is First Login
                        </label>
                        <div className="col-lg-6">
                          <select
                            name="isFirstLogin"
                            onChange={handleChange}
                            className="form-control"
                            value={formData.isFirstLogin || ""}
                          >
                            <option value="">Select</option>
                            <option value="true">True</option>
                            <option value="false">False</option>
                          </select>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      ) : (
        // Listing section
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h4 className="card-title m-0">Inspection Users</h4>
            <div className="d-flex align-items-center gap-2">
              <input
                type="text"
                className="form-control"
                placeholder="Search by name, email or status"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(0);
                }}
              />
            {/* <Can I="create" a="InspectionUser"> */}
              <button
                className="btn btn-secondary btn-md"
                // disabled={
                //   !(
                //     service === "*" &&
                //     role === "*"
                //   ) &&
                //   (!currentRole || availableRoles.length === 0)
                // }
                style={{ whiteSpace: "nowrap" }}
                onClick={handleAdd}
              >
                Add User
              </button>
            {/* </Can> */}
            </div>
          </div>

          <div className="card-body">
            {loading && <p>Loading users...</p>}
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Role</th>
                    {/* <th>Actions</th> */}
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((r, i) => (
                      <tr key={i}>
                        <td>{currentPage * itemsPerPage + i + 1}</td>
                        <td>{r._id?.slice(-5) || "--"}</td>
                        <td>{r.name || "--"}</td>
                        <td>{r.email || "--"}</td>
                        <td>
                          <span
                            className={`badge light ${
                              r.isActive ? "badge-success" : "badge-danger"
                            }`}
                          >
                            <i
                              className={`fa fa-circle me-1 ${
                                r.isActive ? "text-success" : "text-danger"
                              }`}
                            />
                            {r.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>{r.role || "--"}</td>
                        {/* <td>
                          {!(service === "*" && role === "*") &&
                          (!currentRole ||
                            roles.find(
                              (rl) =>
                                rl.role.toLowerCase() ===
                                (r.role || "").toLowerCase(),
                            )?.level <= (currentRole?.level || 999)) ? (
                            // 🚫 Show blocked icon when user cannot modify
                            <span className="text-danger" title="Not allowed">
                              <i className="fa fa-ban"></i>
                            </span>
                          ) : (
                            // ✅ Show dropdown only when allowed
                            <Dropdown>
                              <Dropdown.Toggle
                                variant="link"
                                bsPrefix="no-padding-toggle"
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  padding: 0,
                                }}
                              >
                                &#x2022;&#x2022;&#x2022;
                              </Dropdown.Toggle>
                              <Dropdown.Menu align="end">
                                <Dropdown.Item onClick={() => handleEdit(r)}>
                                  Edit User
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => handleDelete(r._id)}
                                >
                                  Delete User
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          )}
                        </td> */}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center">
                        No users found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                Showing{" "}
                {filteredData.length === 0 ? 0 : currentPage * itemsPerPage + 1}{" "}
                to{" "}
                {Math.min(
                  (currentPage + 1) * itemsPerPage,
                  filteredData.length,
                )}{" "}
                of {filteredData.length} entries
              </div>
              <div>
                <button
                  className={`btn btn-sm btn-outline-primary me-2 ${
                    currentPage === 0 ? "disabled" : ""
                  }`}
                  onClick={() =>
                    currentPage > 0 && setCurrentPage(currentPage - 1)
                  }
                >
                  Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    className={`btn btn-sm border me-2 ${
                      currentPage === i ? "btn-primary text-white" : ""
                    }`}
                    onClick={() => setCurrentPage(i)}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  className={`btn btn-sm btn-outline-primary ${
                    currentPage === totalPages - 1 ? "disabled" : ""
                  }`}
                  onClick={() =>
                    currentPage + 1 < totalPages &&
                    setCurrentPage(currentPage + 1)
                  }
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InspectionUser;
